// Shared FilterDef presets + a generic onSearch builder used across
// entity list views. Pairs with columnBuilders.ts — this file handles
// the filter/search side of the same "reusable primitives" goal.
//
// The goal: avoid re-writing per-entity onSearch handlers that all
// do the same "form value → CrudFilter" mapping.

import React from "react";
import type { CrudFilter } from "@refinedev/core";
import type { FilterDef } from "../../../ds/tables/types";
import { buildGlobalSearchFilter } from "./globalSearchBackend";
import {
  useEntitySelectRenderer,
  type EntitySelectRendererProps,
} from "@ds/display/entitySelectRendererContext";
import { Select as DSSelect, type SelectProps as DSSelectProps } from "@ds/forms/Select";

// The table filter chrome injects `{ value, onChange }` into every
// `FilterDef.component`. Each preset below re-exports that pair to the
// underlying control, so the control's own prop types are the accurate
// contract for the injected props.
type DSSelectFilterProps = Pick<DSSelectProps<string | number>, "value" | "onChange">;

// ── Primitive filter defs ───────────────────────────────────────────────

export const FILTER_IS_ACTIVE: FilterDef = selectFilter({
  name: "is_active",
  label: "Aktywne",
  placeholder: "Aktywne",
  multiple: false,
  minWidth: 120,
  options: [
    { label: "Tak", value: "true" },
    { label: "Nie", value: "false" },
  ],
});

export const FILTER_IS_VALID: FilterDef = selectFilter({
  name: "is_valid",
  label: "Ważne",
  placeholder: "Ważne",
  multiple: false,
  minWidth: 120,
  options: [
    { label: "Tak", value: "true" },
    { label: "Nie", value: "false" },
  ],
});

export const FILTER_CREATED_DATE_RANGE: FilterDef = {
  name: "created_at_range",
  label: "Utworzono",
  type: "date-range",
  field: "created_at",
};

export const FILTER_UPDATED_DATE_RANGE: FilterDef = {
  name: "updated_at_range",
  label: "Zmieniono",
  type: "date-range",
  field: "updated_at",
};

// ── Currency ─────────────────────────────────────────────────────────────

export const FILTER_CURRENCY: FilterDef = selectFilter({
  name: "currency",
  label: "Waluta",
  placeholder: "Waluta",
  multiple: false,
  minWidth: 110,
  options: [
    { label: "PLN", value: "PLN" },
    { label: "EUR", value: "EUR" },
    { label: "USD", value: "USD" },
  ],
});

// ── Builder helpers ─────────────────────────────────────────────────────

/**
 * Filter→query mapping for a Postgres ARRAY column (e.g. companies.categories
 * text[]). The default array-value derivation (`in`) produces a scalar `IN`
 * that PostgREST rejects with 42704 — the whole getList 400s. Array columns
 * filter via `ina` (PostgREST `cs`, contains) instead; several selected values
 * are `or`-joined single-value branches so the semantics stay ANY-of, not
 * contains-ALL.
 */
export function arrayAnyOfToFilter(field: string): (value: unknown) => CrudFilter[] {
  return (value) => {
    const values = (Array.isArray(value) ? value : [value]).filter(
      (v) => v !== undefined && v !== null && v !== "",
    );
    if (values.length === 0) return [];
    const branches = values.map(
      (v): CrudFilter => ({ field, operator: "ina", value: [v] }),
    );
    return branches.length === 1 ? branches : [{ operator: "or", value: branches }];
  };
}

/** Build a Select-based FilterDef from a static options list. */
export function selectFilter(config: {
  name: string;
  label: string;
  placeholder?: string;
  options: Array<{ label: string; value: string | number }>;
  multiple?: boolean;
  minWidth?: number;
  /** Place in the prominent quick strip (vs the advanced drawer). */
  quick?: boolean;
  /** The DB column is a Postgres array — map through `arrayAnyOfToFilter`. */
  arrayColumn?: boolean;
}): FilterDef {
  return {
    name: config.name,
    label: config.label,
    type: "custom",
    ...(config.quick ? { quick: true } : {}),
    ...(config.arrayColumn ? { toFilter: arrayAnyOfToFilter(config.name) } : {}),
    // Canonical DS multiselect (dropdown variant). Same value/onChange/options
    // contract as the AntD Select it replaces; emits `select-option-{value}`
    // testids for e2e. `multiple` defaults true — enum filters are multiselect.
    component: (props: DSSelectFilterProps) =>
      React.createElement<DSSelectProps<string | number>>(DSSelect, {
        variant: "dropdown",
        multiple: config.multiple ?? true,
        allowClear: true,
        // Deterministic e2e handle — the search/combobox input is stamped
        // `filter-${name}-search` (searchable enables the stamp + type-to-filter).
        testId: `filter-${config.name}`,
        searchable: true,
        // Without a floor the quick-strip trigger collapses to icon width and
        // the popup becomes an unreadable sliver (pre-DS defs set the same
        // floor via `style.minWidth`).
        minWidth: config.minWidth ?? 140,
        maxTagCount: config.multiple === false ? undefined : "responsive",
        // No label fallback — `config.label` is an i18n KEY; the drawer renders
        // a translated label above the field, so a key-as-placeholder is noise.
        placeholder: config.placeholder,
        options: config.options,
        ...props,
      }),
  };
}

export function dateRangeFilter(name: string, label: string, opts?: { quick?: boolean }): FilterDef {
  // The form field key is `name` (often `<column>_range`); the query must target
  // the real date column. Strip a trailing `_range` so `buildListFilterHandler`
  // emits gte/lte on the column, not the non-existent `<column>_range`.
  return { name, label, type: "date-range", field: name.replace(/_range$/, ""), ...(opts?.quick ? { quick: true } : {}) };
}

/** Plain text-contains FilterDef. */
export function textFilter(name: string, label: string, opts?: { quick?: boolean }): FilterDef {
  return { name, label, type: "text", ...(opts?.quick ? { quick: true } : {}) };
}

/**
 * Build an EntitySelect-based FilterDef. Covers the recurring "filter by a
 * related entity" case (company / product / employee / wasteCode pickers) so
 * schema fields can declare `filterable: entitySelectFilter({...})` instead of
 * hand-writing an inline component.
 */
export function entitySelectFilter(config: {
  name: string;
  label: string;
  kind: string;
  multiple?: boolean;
  placeholder?: string;
  minWidth?: number;
  context?: string;
  category?: string;
  categories?: string[];
  role?: string;
  onlyFromProducts?: boolean;
  onlyEdprEligible?: boolean;
  visibleTo?: string[];
  /** Place in the prominent quick strip (vs the advanced drawer). */
  quick?: boolean;
}): FilterDef {
  return {
    name: config.name,
    label: config.label,
    type: "custom",
    ...(config.quick ? { quick: true } : {}),
    ...(config.visibleTo ? { visibleTo: config.visibleTo as FilterDef["visibleTo"] } : {}),
    component: function EntitySelectFilterField(props: Partial<EntitySelectRendererProps>) {
      const renderSelect = useEntitySelectRenderer();
      return renderSelect({
        kind: config.kind,
        allowClear: true,
        // Deterministic e2e handle on the Select root — same contract as
        // selectFilter's `filter-${name}`. Without it, drawer specs can only
        // target the Form.Item wrapper, whose center-click lands on the label.
        testId: `filter-${config.name}`,
        ...(config.multiple ? { mode: "multiple", maxTagCount: "responsive" } : {}),
        placeholder: config.placeholder,
        style: { minWidth: config.minWidth ?? 160 },
        ...(config.context ? { context: config.context } : {}),
        ...(config.category ? { category: config.category } : {}),
        ...(config.categories ? { categories: config.categories } : {}),
        ...(config.role ? { role: config.role } : {}),
        ...(config.onlyFromProducts ? { onlyFromProducts: true } : {}),
        ...(config.onlyEdprEligible ? { onlyEdprEligible: true } : {}),
        ...props,
      }) as React.ReactElement | null;
    },
  };
}

// ── Generic onSearch builder ────────────────────────────────────────────
//
// Declaratively describe how each filter form value maps to CrudFilter(s).
// Handles: eq, in, gte, lte, ilike, date-range, bool parsing, server search.

type FieldMapping =
  | {
      /** form field name */
      name: string;
      /** DB column */
      field: string;
      operator: "eq" | "ne" | "contains" | "startswith";
      /** Convert "true"/"false" strings to real booleans. */
      parseBool?: boolean;
    }
  | {
      name: string;
      field: string;
      operator: "in";
    }
  | {
      name: string;
      /** Two underlying DB columns — [startField, endField]. */
      field: string;
      operator: "date-range";
    }
  | {
      name: string;
      /** Routes through global_search RPC with this entity key. */
      operator: "search";
      entity: string;
    }
  | {
      /** Escape hatch: custom mapper. Return [] to skip. */
      name: string;
      operator: "custom";
      build: (value: unknown) => CrudFilter[] | Promise<CrudFilter[]>;
    };

const isEmpty = (v: unknown): boolean =>
  v === undefined ||
  v === null ||
  v === "" ||
  (Array.isArray(v) && v.length === 0);

/**
 * Ant Design's RangePicker delivers dayjs objects; anything else (an ISO
 * string already, say) passes through untouched. Structural check keeps this
 * helper free of a dayjs import.
 */
const toIsoDate = (v: unknown): unknown => {
  const fmt = (v as { format?: unknown } | null)?.format;
  return typeof fmt === "function"
    ? (v as { format: (template: string) => string }).format("YYYY-MM-DD")
    : v;
};

/**
 * Build an `onSearch` handler from a declarative mapping list.
 *
 * Example:
 *   makeOnSearch([
 *     { name: "search",  operator: "search", entity: "companies" },
 *     { name: "nip",     field: "nip",        operator: "contains" },
 *     { name: "country", field: "country_code", operator: "in" },
 *     { name: "is_active", field: "is_active",  operator: "eq", parseBool: true },
 *     { name: "created_at_range", field: "created_at", operator: "date-range" },
 *   ])
 */
export function makeOnSearch(
  mappings: FieldMapping[],
): (values: Record<string, unknown>) => Promise<CrudFilter[]> {
  return async (values) => {
    const filters: CrudFilter[] = [];

    for (const m of mappings) {
      const raw = values?.[m.name];
      if (isEmpty(raw)) continue;

      if (m.operator === "search") {
        const searchFilters = await buildGlobalSearchFilter(String(raw), m.entity);
        if (Array.isArray(searchFilters)) filters.push(...searchFilters);
        continue;
      }

      if (m.operator === "custom") {
        const result = await Promise.resolve(m.build(raw));
        filters.push(...result);
        continue;
      }

      if (m.operator === "date-range") {
        // Ant Design RangePicker delivers [dayjs, dayjs]
        const [start, end]: [unknown, unknown] = Array.isArray(raw)
          ? [raw[0] as unknown, raw[1] as unknown]
          : [null, null];
        if (start) {
          filters.push({
            field: m.field,
            operator: "gte",
            value: toIsoDate(start),
          });
        }
        if (end) {
          filters.push({
            field: m.field,
            operator: "lte",
            value: toIsoDate(end),
          });
        }
        continue;
      }

      if (m.operator === "in") {
        filters.push({
          field: m.field,
          operator: "in",
          value: Array.isArray(raw) ? raw : [raw],
        });
        continue;
      }

      // eq / ne / contains / startswith
      let value: unknown = raw;
      if ("parseBool" in m && m.parseBool) {
        value = raw === true || raw === "true" ? true : raw === false || raw === "false" ? false : raw;
      }

      filters.push({
        field: m.field,
        operator: m.operator,
        value,
      });
    }

    return filters;
  };
}
